<template>
  <vl-region>
    <vl-layout>
      <PbsGoBackLink />
      <template v-if="!loading">
        <vl-grid>
          <vl-column width="8" width-xs="12">
            <vl-title tag-name="h1"> {{ $t('productgroup.detail.title') }} </vl-title>
          </vl-column>
          <vl-column width="4" width-xs="12">
            <vl-action-group
              v-if="detail && !editStore.isEdit(ProductGroupEditType.ProductGroup, detail.id)"
              mod-align-right
              mod-space
              between
            >
              <vl-button
                icon="pencil"
                mod-icon-before
                mod-secondary
                :mod-disabled="editStore.editing"
                @click="editStore.openEdit(ProductGroupEditType.ProductGroup, detail.id)"
              >
                {{ $t('general.edit') }}
              </vl-button>
              <vl-button icon="trash" mod-icon-before mod-error @click="showDeleteModal">{{
                $t('general.delete')
              }}</vl-button>
            </vl-action-group>
            <vl-action-group v-else-if="detail" mod-align-right mod-space between>
              <vl-button icon="save" :mod-loading="reloading" mod-icon-before @click="save">{{
                $t('general.save')
              }}</vl-button>
              <vl-button
                icon="cross"
                :mod-loading="reloading"
                mod-icon-before
                mod-secondary
                mod-error
                @click="editStore.closeEdit"
              >
                {{ $t('general.cancel') }}
              </vl-button>
            </vl-action-group>
          </vl-column>
        </vl-grid>
        <vl-grid v-if="detail" class="vl-u-spacer-bottom--medium">
          <vl-column>
            <ProductGroupEdit v-if="editStore.isEdit(ProductGroupEditType.ProductGroup, detail.id)" ref="editForm" />
            <ProductGroupRead v-else :detail="detail" />
          </vl-column>
        </vl-grid>
        <vl-grid v-else-if="notFound">
          <vl-column>
            <vl-alert icon="warning" :title="$t('productgroup.detail.notFound.title')" mod-warning>
              <p>{{ $t('productgroup.detail.notFound.content', { id }) }}</p>
            </vl-alert>
          </vl-column>
        </vl-grid>
      </template>
      <template v-if="loading">
        <vl-region>
          <div v-vl-align:center>
            <vl-loader :message="$t('productgroup.detail.loading')" />
          </div>
        </vl-region>
      </template>
    </vl-layout>
  </vl-region>
</template>
<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute, onBeforeRouteLeave } from 'vue-router';
import { useProductGroupDetailStore } from '../../store/product-group/product-group-detail.store';
import { useProductGroupEditStore, ProductGroupEditType } from '../../store/product-group/product-group-edit.store';
import { ProductGroupDetailDto } from '@/api/backoffice/clients';
import ProductGroupRead from './ProductGroupRead.vue';
import ProductGroupEdit from './ProductGroupEdit.vue';
import PbsGoBackLink from '@/components/PbsGoBackLink.vue';
import { useModalStore } from '@/modules/core/store/modal.store';
import { useI18n } from 'vue-i18n';

const store = useProductGroupDetailStore();
const editStore = useProductGroupEditStore();
const modalStore = useModalStore();

const { t } = useI18n();

const route = useRoute();

const editForm = ref();

const detail = computed((): ProductGroupDetailDto => {
  return store.detail;
});

const loading = computed((): boolean => {
  return store.loading;
});

const reloading = computed((): boolean => {
  return store.reloading;
});

const notFound = computed((): boolean => {
  return store.notFound;
});

const id = computed((): number => {
  return parseInt(route.params.id as string, 10);
});

onMounted(async () => {
  editStore.init();
  await store.get(id.value);
});

const save = () => {
  editForm.value.onSubmit();
};

const showDeleteModal = () => {
  modalStore.showDeleteModal({
    title: t('productgroup.detail.delete.title'),
    props: {
      navigationRouteOnSuccess: { name: 'configuration', hash: '#productgroup' },
      confirm: () => store.delete(id.value)
    }
  });
};

onBeforeRouteLeave((to, from, next) => {
  store.clear();
  next();
});
</script>
